import React, { useState, useEffect } from "react";
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Spinner,
    Input,
    Label,
    Alert,
    FormFeedback,
} from "reactstrap";
import { markRefund } from "../../../api/requests/subscription-requests";
import { toast } from "react-toastify";

const formatPrice = (price) =>
    new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(price || 0) + " ₺";

/**
 * RefundModal - Abonelik iade işaretleme modali
 *
 * Props
 * ─────────────────────────────────────────────────────────
 * @param {boolean}  isOpen        - Modal açık mı?
 * @param {Function} toggle        - Modal'ı kapat/aç
 * @param {Object}   subscription  - İade edilecek abonelik (id, planName, paidAmount)
 * @param {Function} onConfirm     - Başarılı işlem sonrası callback
 */
const RefundModal = ({ isOpen, toggle, subscription, onConfirm }) => {
    const [refundAmount, setRefundAmount] = useState("");
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (isOpen && subscription) {
            setRefundAmount(subscription.paidAmount != null ? String(subscription.paidAmount) : "");
            setReason("");
            setErrors({});
        }
    }, [isOpen, subscription]);

    if (!subscription) return null;

    const validate = () => {
        const newErrors = {};
        const amount = parseFloat(refundAmount);
        if (!refundAmount || isNaN(amount) || amount <= 0) {
            newErrors.refundAmount = "İade tutarı 0'dan büyük olmalıdır";
        } else if (subscription.paidAmount != null && amount > parseFloat(subscription.paidAmount)) {
            newErrors.refundAmount = `İade tutarı ödenen tutarı (${formatPrice(subscription.paidAmount)}) aşamaz`;
        }
        if (!reason.trim()) {
            newErrors.reason = "İade sebebi zorunludur";
        }
        return newErrors;
    };

    const handleConfirm = async () => {
        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }
        setErrors({});
        try {
            setSaving(true);
            await markRefund({
                subscriptionId: subscription.id,
                refundAmount: parseFloat(refundAmount),
                reason: reason.trim(),
            });
            toast.success("Abonelik iade olarak işaretlendi");
            toggle();
            if (onConfirm) onConfirm();
        } catch (err) {
            toast.error(err.message || "İade işaretlenemedi");
        } finally {
            setSaving(false);
        }
    };

    const handleClose = () => {
        if (!saving) {
            setRefundAmount("");
            setReason("");
            setErrors({});
            toggle();
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={handleClose} centered>
            <ModalHeader toggle={handleClose} className="border-bottom">
                <div className="d-flex align-items-center gap-2 text-warning">
                    <i className="bx bx-money fs-4"></i>
                    İade İşareti
                </div>
            </ModalHeader>

            <ModalBody className="py-4">
                <Alert color="warning" className="mb-3 d-flex align-items-start gap-2">
                    <i className="bx bx-info-circle mt-1 flex-shrink-0" />
                    <span>
                        Bu işlem sadece sisteme kayıt amaçlıdır. PayTR üzerinden iade ayrıca yapılmalıdır.
                    </span>
                </Alert>

                <div className="border rounded p-3 bg-light mb-3">
                    <div className="d-flex justify-content-between mb-2">
                        <span className="text-muted">Plan Adı:</span>
                        <span className="fw-semibold">{subscription.planName}</span>
                    </div>
                    <div className="d-flex justify-content-between">
                        <span className="text-muted">Ödenen Tutar:</span>
                        <span className="fw-semibold text-success">{formatPrice(subscription.paidAmount)}</span>
                    </div>
                </div>

                <div className="mb-3">
                    <Label className="text-muted mb-1 fw-semibold" style={{ fontSize: ".9rem" }}>
                        İade Tutarı <span className="text-danger">*</span>
                    </Label>
                    <Input
                        type="number"
                        step="0.01"
                        min="0.01"
                        max={subscription.paidAmount}
                        value={refundAmount}
                        onChange={(e) => {
                            setRefundAmount(e.target.value);
                            if (errors.refundAmount) setErrors((prev) => ({ ...prev, refundAmount: undefined }));
                        }}
                        invalid={!!errors.refundAmount}
                        disabled={saving}
                        placeholder="0.00"
                    />
                    {errors.refundAmount && <FormFeedback>{errors.refundAmount}</FormFeedback>}
                </div>

                <div>
                    <Label className="text-muted mb-1 fw-semibold" style={{ fontSize: ".9rem" }}>
                        Sebep <span className="text-danger">*</span>
                    </Label>
                    <Input
                        type="textarea"
                        rows={3}
                        placeholder="İade sebebini belirtiniz..."
                        value={reason}
                        onChange={(e) => {
                            setReason(e.target.value);
                            if (errors.reason) setErrors((prev) => ({ ...prev, reason: undefined }));
                        }}
                        invalid={!!errors.reason}
                        maxLength={500}
                        disabled={saving}
                    />
                    {errors.reason && <FormFeedback>{errors.reason}</FormFeedback>}
                </div>
            </ModalBody>

            <ModalFooter className="border-top">
                <div className="d-flex justify-content-end gap-2 w-100">
                    <Button
                        color="secondary"
                        onClick={handleClose}
                        disabled={saving}
                        className="px-4"
                    >
                        Vazgeç
                    </Button>
                    <Button
                        color="warning"
                        onClick={handleConfirm}
                        disabled={saving}
                        className="px-4 d-flex align-items-center gap-2"
                        style={{ minWidth: "150px" }}
                    >
                        {saving ? (
                            <>
                                <Spinner size="sm" />
                                İşleniyor...
                            </>
                        ) : (
                            <>
                                <i className="bx bx-money"></i>
                                İade İşaretle
                            </>
                        )}
                    </Button>
                </div>
            </ModalFooter>
        </Modal>
    );
};

export default RefundModal;
